import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import { toast } from '@flash-global66/g-toast';
import { getReasonMessage, teamUserService } from '@/shared/common';
import { logger } from '@/shared/common/utils/logger.util';
import { useWalletStore } from '@/stores/wallet';
import type { WalletType } from '@/types/WalletType';
import type {
  AccountsFetchOptions,
  AccountsLoadFailureLog,
  AdditionalAccountEditPayload,
  AdditionalAccountEditResult,
  AdditionalAccountWallet,
  CardAccountReassignment,
  CompanyAccountsCatalogResultSource,
  UserAccountAssignment,
} from '../types/additionalAccount.type';
import type { AdditionalAccountStoreState, FetchAssignedUsersOptions } from '../types/additionalAccountStore.type';
import type { AssignedCompanyUserOption } from '../types/companyUsers.type';
import type { CreateAdditionalAccountPayload } from '../types/createAdditionalAccount.type';
import {
  DELETE_ADDITIONAL_ACCOUNT_SUCCESS_TOAST,
  ASSIGN_ADDITIONAL_ACCOUNT_USERS_SUCCESS_TOAST,
  REMOVE_ADDITIONAL_ACCOUNT_USER_SUCCESS_TOAST,
  CREATE_ADDITIONAL_ACCOUNT_FUNDING_FAILED_TOAST,
  CREATE_ADDITIONAL_ACCOUNT_ASSIGN_FAILED_TOAST,
} from '../constants/additionalAccount.constant';
import {
  ACCOUNT_ASSIGNED_USERS_MAX_PAGES,
  ACCOUNT_ASSIGNED_USERS_PAGE_SIZE,
  ACCOUNT_ASSIGNMENT_USER_LEVELS,
} from '../constants/companyUsers.constant';
import { buildCreatedAdditionalAccountWallet } from '../utils/createAdditionalAccountWallet.util';
import { buildAdditionalAccountClosureRequest } from '../utils/buildAdditionalAccountClosureRequest.util';
import { selectMainWalletPerCurrency } from '../utils/selectMainWalletPerCurrency.util';
import { getAdditionalAccountsAdapter } from '../adapters/additionalAccount.adapter';
import {
  getAssignedAccountIdsAdapter,
  hasAssignedMainAccountAdapter,
} from '../adapters/companyAccountsCatalog.adapter';
import { getAdditionalAccountAvailabilityAdapter } from '../adapters/additionalAccountAvailability.adapter';
import { getAssignedCompanyUserAdapter } from '../adapters/companyUser.adapter';
import { additionalAccountService } from '../services/additionalAccount.service';
import {
  ErrorsAdditionalAccount,
  DELETE_ADDITIONAL_ACCOUNT_ERROR_MESSAGE,
  CREATE_ADDITIONAL_ACCOUNT_ERROR_MESSAGE,
  UNASSIGN_USER_ACCOUNT_ERROR_MESSAGE,
} from '../errors/additionalAccount.error';

/**
 * Traduce ids del catálogo (string numérico, `getCompanyUserAdapter` hace
 * `String(userId)`) al `number` que piden los endpoints de asignación —
 * `createAdditionalAccount`, `assignUsersToAdditionalAccount`,
 * `unassignUserFromAccount` y el diff de `applyAccountEdit` comparten esta
 * conversión. Ids no numéricos se descartan: si el resultado es más corto que
 * la entrada, se asignó de menos.
 */
function toCreatableUserIds(userIds: string[]): number[] {
  return userIds.filter((userId) => Number.isInteger(Number(userId))).map(Number);
}

/** @returns `false` solo si había que pedir `/wallets` y el pedido falló. */
async function loadWalletsIfMissing(): Promise<boolean> {
  const walletStore = useWalletStore();
  if (walletStore.wallets?.length) {
    return true;
  }
  return walletStore.setWallets();
}

/** @returns `false` si el pedido a `/wallets` falló. */
function reloadWallets(): Promise<boolean> {
  return useWalletStore().setWallets();
}

function logAccountsLoadFailure({ isUiStateTracked, message, context, error }: AccountsLoadFailureLog): void {
  if (!isUiStateTracked) {
    logger.warn(message, context);
    return;
  }
  if (error) {
    logger.error(message, context, error);
    return;
  }
  logger.error(message, context);
}

function buildInitialAdditionalAccountState(): AdditionalAccountStoreState {
  return {
    accounts: [],
    assignedAccountIds: [],
    hasAssignedMainAccount: false,
    isFetchingAccounts: false,
    hasLoadedAccounts: false,
    hasAccountsLoadError: false,
    fetchGeneration: 0,
    fetchAccountsInFlight: null,
    isCreatingAccount: false,
    locallyCreatedAccountIds: [],
    availabilityByCurrency: {},
    hasLoadedAvailability: false,
    fetchAvailabilityInFlight: null,
    assignedUsersByAccount: {},
    isFetchingAssignedUsersByAccount: {},
    assignedUsersErrorByAccount: {},
    fetchAssignedUsersInFlightByAccount: {},
    hasLoadedAssignedUsersByAccount: {},
    fetchAssignedUsersSweepTokenByAccount: {},
  };
}

/**
 * Estado compartido de cuentas adicionales (ML-45, hito 1): el panel del Home,
 * la página de gestión y el detalle (`AdditionalWalletDetail`) — 3 módulos —
 * comparten el MISMO listado, por eso vive en `src/shared/` como store Pinia
 * (regla `pinia-stores.md`) y no dentro de un módulo. Al estar en Pinia,
 * `resetAllStores()` del logout lo limpia sin el reset manual que necesitaba
 * el ref module-scoped anterior.
 *
 * Estado de integración al 2026-09-16 — CUENTAS y USUARIOS ya son reales de
 * punta a punta: lectura (`fetchAssignedUsers`, ML-96) y las tres formas de
 * escritura (al crear, y `assignUsersToAccount`/`removeUserFromAccount`/
 * `applyAccountEdit` por fuera del create, también ML-96):
 * - **Listado — REAL, un solo GET (`grouped-by-currency`)**: `fetchAdditionalAccounts()`
 *   consume `GET .../additional-accounts/grouped-by-currency` una única vez para
 *   TODAS las monedas — reemplazó al `GET .../additional-accounts?currencyCode=`
 *   por moneda que disparaba un `Promise.all` (la cascada que prohíbe
 *   `http-cascade.md`) y luego al `/filtered`, que el backend eliminó. El
 *   backend ya filtra: devuelve solo las cuentas que el usuario puede operar
 *   (`isAssigned` dejó de existir), así que `assignedAccountIds` son todas las
 *   del response (`isAccountAssigned`, `hasAssignedAccounts`).
 * - **Creación — REAL (ML-3)**: `POST .../additional-accounts`. El fondeo
 *   inicial es asíncrono y su fallo no revierte la cuenta (ver `createAccount`).
 * - **Cierre — REAL (ML-41, `POST .../closure`)**: reasigna y elimina en un
 *   solo request.
 * - **Renombre — REAL (ML-40)**: ocurre en el drawer de edición, antes de
 *   llegar a `applyAccountEdit`, que solo refleja el resultado en el estado.
 * - **Asignar AL CREAR — REAL (ML-8)**: va en el MISMO
 *   `POST .../additional-accounts`, en el campo `userIds`. No hay segunda llamada
 *   ni estado intermedio: si el create falla, no se asignó nadie.
 * - **Catálogo de usuarios — REAL**: `useCompanyUsersCatalog` sobre
 *   `GET company/b2b/teams/users`, paginado.
 * - **Lectura de asignados — REAL (ML-96)**: `fetchAssignedUsers(accountId)`
 *   barre `GET company/b2b/teams/users` página a página con `accountId` y
 *   `levels` (niveles 1-3, ML-168) hasta la última, y se queda con
 *   `associatedToAccount === true`. El backend NO filtra por `accountId`:
 *   decora cada fila de los usuarios de esos niveles, así que no alcanza con
 *   la primera página.
 * - **Asignar/quitar fuera del create — REAL (ML-96)**: `assignUsersToAccount`
 *   llama a `assignUsersToAdditionalAccount` y `removeUserFromAccount` a
 *   `unassignUserFromAccount`; `applyAccountEdit` (edición batch, ML-50)
 *   calcula el diff contra el caché de `fetchAssignedUsers` y manda un POST
 *   de altas más un `unassignUserFromAccount` por baja, en paralelo. Las tres
 *   refrescan `assignedUsersByAccount` al terminar — la asimetría
 *   "sobrevive a un F5 solo si se asignó al crear" queda cerrada.
 *   `assignedUsersByAccount` es la única fuente de verdad de la asignación;
 *   el espejo optimista que vivió en el campo `assignedUserIds` de cada cuenta
 *   se eliminó una vez que los seis lectores de negocio migraron al caché.
 *
 *   OJO con el número: **ML-69 es otra cosa y ya está en producción** — extendió
 *   `teams/users` con las cuentas DE CADA USUARIO (`accounts[]`, lo que consume
 *   `modules/team`). ML-96 es la dirección inversa: los usuarios DE UNA CUENTA.
 *   Y no llega como endpoint nuevo, sino como un filtro sobre ese mismo
 *   `company/b2b/teams/users` que ya consume `useCompanyUsersCatalog`.
 *
 * OJO: el estado por-página (drawer de creación, loading del cierre) NO vive
 * acá — es de cada instancia de la fachada `useAdditionalAccounts`.
 */
export const useAdditionalAccountStore = defineStore('additionalAccount', {
  state: (): AdditionalAccountStoreState => buildInitialAdditionalAccountState(),

  getters: {
    hasAccounts(state): boolean {
      return state.accounts.length > 0;
    },

    hasAssignedAccounts(state): boolean {
      return state.assignedAccountIds.length > 0;
    },

    /** Lookup O(1): el `Set` se deriva una vez por cambio de `assignedAccountIds`, no por llamada. */
    isAccountAssigned(state): (walletId: number) => boolean {
      const assignedIds = new Set(state.assignedAccountIds);
      return (walletId) => assignedIds.has(walletId);
    },
  },

  actions: {
    /**
     * Override del `$reset` nativo de Pinia (las actions pisan al built-in):
     * el nativo repondría `fetchGeneration` a 0 y una continuación despachada
     * en la generación 0 volvería a validar tras el reset. Acá la generación
     * INCREMENTA — toda continuación en vuelo queda invalidada.
     * `resetAllStores()` del logout lo invoca de forma genérica.
     */
    $reset(): void {
      const nextGeneration = this.fetchGeneration + 1;
      Object.assign(this, buildInitialAdditionalAccountState());
      this.fetchGeneration = nextGeneration;
    },

    /**
     * Un solo GET a `.../grouped-by-currency` para TODAS las monedas del
     * usuario, adaptado y mergeado — reemplaza el `Promise.all`
     * con un GET por moneda que prohíbe `http-cascade.md`. De la misma
     * respuesta salen las cuentas adicionales del usuario y los
     * ids asignados (`assignedAccountIds`).
     * Preserva la semántica multi-moneda que consumen el panel, la gestión
     * (filtro client-side) y el detalle (find por walletId). Corre UNA vez
     * por sesión: tras cargar no refetchea (para saldos frescos está
     * `refreshAdditionalAccounts`); tras un fallo TOTAL queda
     * rearmado para reintentar. Un fetch tardío que todavía no trae una cuenta
     * de `locallyCreatedAccountIds` no la saca de `accounts` ni de
     * `assignedAccountIds` (`createAccount` la asignó: solo nivel 4 crea).
     *
     * Con un solo request ya no existe "fallo parcial": el `Left` deja
     * `hasAccountsLoadError = true` sin tocar `this.accounts`,
     * `this.assignedAccountIds` ni `this.hasAssignedMainAccount` — la señal es
     * el estado persistente con retry (`AdditionalAccountsLoadError`), no un
     * toast.
     *
     * Un grupo de moneda sin wallet principal en el `Map` es normal: el
     * usuario puede tener asignadas adicionales sin su principal. El adapter
     * las arma sin riel bancario y acá solo se deja rastro con `logger.info`.
     *
     * @param prefetchedResult - respuesta de `grouped-by-currency` que el llamador
     * ya pidió (`useCompanyAccountsCatalog`): evita el segundo GET. Se recibe el
     * `ApiResult` entero y no solo el éxito para que un `Left` siga el mismo
     * `fold` de error de abajo. Solo cuenta si el store NO cargó y NO hay fetch
     * en vuelo; en cualquier otro caso se ignora y la action se comporta como
     * sin argumento.
     */
    async fetchAdditionalAccounts(prefetchedResult?: CompanyAccountsCatalogResultSource): Promise<void> {
      if (this.hasLoadedAccounts) {
        return;
      }
      if (this.fetchAccountsInFlight) {
        return this.fetchAccountsInFlight;
      }
      return this.runAccountsFetch({ prefetchedResult, loadWallets: loadWalletsIfMissing, isUiStateTracked: true });
    },

    /**
     * Vuelve a pedir wallets y catálogo con el store ya cargado: los saldos de
     * las cuentas adicionales quedan congelados tras enviar o convertir si solo
     * corre `fetchAdditionalAccounts`. Se comporta como el primer fetch: prende
     * `isFetchingAccounts`, limpia `hasAccountsLoadError` al arrancar y lo marca
     * ante un fallo, sin borrar `accounts` ni `assignedAccountIds`. Dedupea
     * contra un fetch en vuelo.
     *
     * @param prefetchedResult - respuesta de `grouped-by-currency` ya pedida por
     * el llamador, o su promesa: evita el segundo GET y, si aún está pendiente,
     * prende el loading y pide `/wallets` en paralelo.
     */
    async refreshAdditionalAccounts(prefetchedResult?: CompanyAccountsCatalogResultSource): Promise<void> {
      if (this.fetchAccountsInFlight) {
        return this.fetchAccountsInFlight;
      }
      return this.runAccountsFetch({ prefetchedResult, loadWallets: reloadWallets, isUiStateTracked: true });
    },

    /**
     * Actualiza los DATOS de las cuentas sin tocar el estado global de UI: con el
     * store ya cargado no prende `isFetchingAccounts` ni marca
     * `hasAccountsLoadError`, y ante un `Left`, un throw o `/wallets` caído sin wallets conserva
     * lo que hay y deja un `logger.warn`. Reusa las wallets cargadas
     * (`loadWalletsIfMissing`). Sin datos previos se comporta como
     * `fetchAdditionalAccounts`. Dedupea contra una corrida en vuelo.
     *
     * @param prefetchedResult - respuesta de `grouped-by-currency` ya pedida por
     * el llamador, o su promesa: evita el segundo GET.
     */
    async syncAdditionalAccounts(prefetchedResult?: CompanyAccountsCatalogResultSource): Promise<void> {
      if (this.fetchAccountsInFlight) {
        return this.fetchAccountsInFlight;
      }
      return this.runAccountsFetch({
        prefetchedResult,
        loadWallets: loadWalletsIfMissing,
        isUiStateTracked: !this.hasLoadedAccounts,
      });
    },

    /**
     * Cuerpo común de `fetchAdditionalAccounts`, `refreshAdditionalAccounts` y
     * `syncAdditionalAccounts`. Pide `/wallets` y el catálogo en paralelo: el cruce
     * es lo único que necesita las dos respuestas. Con `isUiStateTracked` prende `isFetchingAccounts`,
     * limpia el error al arrancar y un fallo marca `hasAccountsLoadError`; sin él
     * no toca esos flags y el fallo solo deja un `logger.warn`. En ambos casos
     * conserva los datos previos.
     */
    async runAccountsFetch(options: AccountsFetchOptions): Promise<void> {
      const { prefetchedResult, loadWallets, isUiStateTracked } = options;
      const walletStore = useWalletStore();
      const generation = this.fetchGeneration;
      const isStale = (): boolean => generation !== this.fetchGeneration;
      const markLoadError = (): void => {
        if (isUiStateTracked) {
          this.hasAccountsLoadError = true;
        }
      };
      function logLoadFailure(message: string, context?: Record<string, unknown>, error?: Error): void {
        logAccountsLoadFailure({ isUiStateTracked, message, context, error });
      }

      const fetchRun = (async () => {
        if (isUiStateTracked) {
          this.isFetchingAccounts = true;
          this.hasAccountsLoadError = false;
        }
        try {
          const [didLoadWallets, result] = await Promise.all([
            loadWallets(),
            prefetchedResult ?? additionalAccountService.getAccountsGroupedByCurrency(),
          ]);
          if (isStale()) {
            return;
          }
          const mainWallets = selectMainWalletPerCurrency(walletStore.wallets);
          // Cero principales con `/wallets` OK es un usuario sin principales asignadas y el
          // catálogo vale igual. Con `/wallets` caído no hay de dónde clonar el riel bancario:
          // el catálogo ya pedido se descarta y, sin toast, la señal es el error persistente
          // (`AdditionalAccountsLoadError`).
          if (mainWallets.length === 0 && !didLoadWallets) {
            if (!isUiStateTracked) {
              logger.warn('additionalAccount.store: /wallets falló y no hay wallets para cruzar el catálogo');
            }
            markLoadError();
            return;
          }
          const mainWalletsByCurrency = new Map<string, WalletType>(
            mainWallets.map((wallet) => [wallet.currency, wallet])
          );

          result.fold(
            (failure) => {
              // Fallo TOTAL: señal PERSISTENTE en la UI (AdditionalAccountsLoadError
              // con retry), no un toast autodescartable que dejaría a la vista
              // afirmando "sin cuentas".
              logLoadFailure('additionalAccount.store: la carga de cuentas no pudo obtener el catálogo', { failure });
              markLoadError();
            },
            (success) => {
              const returnedCurrencies = success.data.currencies ?? [];
              const missingMainWalletCurrencies = returnedCurrencies
                .map((group) => group.currencyCode)
                .filter((currency) => !mainWalletsByCurrency.has(currency));
              if (missingMainWalletCurrencies.length > 0) {
                logger.info(
                  'additionalAccount.store: monedas sin wallet principal, sus adicionales quedan sin riel bancario',
                  { currencies: missingMainWalletCurrencies }
                );
              }

              const fetchedAccounts = getAdditionalAccountsAdapter(success.data, mainWalletsByCurrency);

              // Un fetch que resuelve tarde no debe borrar las cuentas creadas
              // en esta sesión (ML-3): un catálogo anterior a la creación no la
              // trae. Se excluyen las que el backend YA devolvió para no
              // duplicarlas.
              const fetchedAccountIds = new Set(fetchedAccounts.map((account) => account.wallet.walletId));
              const locallyCreatedIds = new Set(this.locallyCreatedAccountIds);
              const locallyCreatedAccounts = this.accounts.filter(
                (account) =>
                  locallyCreatedIds.has(account.wallet.walletId) && !fetchedAccountIds.has(account.wallet.walletId)
              );
              this.accounts = [...fetchedAccounts, ...locallyCreatedAccounts];
              const preservedAssignedIds = this.assignedAccountIds.filter(
                (walletId) => locallyCreatedIds.has(walletId) && !fetchedAccountIds.has(walletId)
              );
              this.assignedAccountIds = [
                ...new Set([...getAssignedAccountIdsAdapter(success.data), ...preservedAssignedIds]),
              ];
              this.hasAssignedMainAccount = hasAssignedMainAccountAdapter(success.data);
              this.hasLoadedAccounts = true;
            }
          );
        } catch (error) {
          // Defensa ante throws inesperados (p.ej. un 200 con body fuera de
          // contrato): la promesa NUNCA rechaza — un rechazo cortaría el
          // `await` del onMounted del detalle sin redirect ni feedback. Queda
          // fuera del contrato de `executeRequest`, así que sin este log no
          // deja rastro en ningún lado.
          logLoadFailure(
            'additionalAccount.store: la carga de cuentas lanzó fuera del contrato',
            undefined,
            error instanceof Error ? error : undefined
          );
          if (!isStale()) {
            markLoadError();
          }
        } finally {
          // Una continuación stale no toca el estado de la sesión nueva: el
          // reset ya limpió los flags y puede haber un fetch nuevo en vuelo.
          if (!isStale()) {
            if (isUiStateTracked) {
              this.isFetchingAccounts = false;
            }
            this.fetchAccountsInFlight = null;
          }
        }
      })();

      this.fetchAccountsInFlight = markRaw(fetchRun);
      return fetchRun;
    },

    /**
     * `undefined` cuando la moneda no está en el catálogo de disponibilidad o
     * el backend no declaró un límite real (`maxAdditionalAccounts: null`). El
     * llamador (`useAdditionalAccountsManagement`) cae a
     * `DEFAULT_ADDITIONAL_ACCOUNTS_LIMIT` con `??` — un límite de `0` es
     * legítimo y tiene que preservarse, no colapsar al default.
     */
    getAdditionalAccountsLimit(currency: string): number | undefined {
      return this.availabilityByCurrency[currency?.toUpperCase()]?.maxAdditionalAccounts ?? undefined;
    },

    /**
     * Configuración país-moneda con DOS consumidores: el paso `intro` del
     * drawer de creación y el contador `N/límite` de la pantalla de gestión,
     * que lee `maxAdditionalAccounts` vía `getAdditionalAccountsLimit`. Corre
     * UNA vez por sesión: es una tabla de configuración, no estado del usuario.
     *
     * Se pide con `country` y sin `currency` a propósito: una sola llamada trae
     * todas las monedas del país, así que cubre cualquier moneda que el usuario
     * elija sin refetch al volver atrás en el drawer.
     *
     * Falla en silencio — sin toast y sin mapa de errores. Asimetría deliberada
     * contra `fetchAdditionalAccounts`, que sí avisa porque su fallo deja una
     * lista vacía que miente. Acá el costo es menor pero **no es cero**: sin
     * este dato el contador de cada card cae a `DEFAULT_ADDITIONAL_ACCOUNTS_LIMIT`
     * sin señal de que no viene del backend. Se acepta porque la pantalla sigue
     * operativa y `executeRequest` ya dejó el rastro en el logger y en Sentry.
     * Al no marcar `hasLoadedAvailability`, la próxima apertura del drawer
     * reintenta.
     * @param country - ISO2 del país de la empresa. Vacío NO llama: sin params el backend devuelve el catálogo global y dos países con la misma moneda colisionarían al indexar.
     */
    async fetchAdditionalAccountAvailability(country: string): Promise<void> {
      if (!country || this.hasLoadedAvailability) {
        return;
      }
      if (this.fetchAvailabilityInFlight) {
        return this.fetchAvailabilityInFlight;
      }

      const generation = this.fetchGeneration;
      const isStale = (): boolean => generation !== this.fetchGeneration;

      const fetchRun = (async () => {
        try {
          const result = await additionalAccountService.getAdditionalAccountAvailability({
            country: country.toUpperCase(),
          });
          if (isStale()) {
            return;
          }
          result.fold(
            () => {
              // Silencio deliberado: ver el JSDoc de la action.
            },
            (success) => {
              this.availabilityByCurrency = getAdditionalAccountAvailabilityAdapter(success.data);
              this.hasLoadedAvailability = true;
            }
          );
        } catch (error) {
          // La llamada es fire-and-forget desde un watch: un reject sería un
          // unhandled rejection. Mismo criterio que el catch del listado — se
          // degrada en silencio para el usuario, pero deja rastro en el log.
          logger.error(
            'additionalAccount.store: fetchAdditionalAccountAvailability lanzó fuera del contrato',
            undefined,
            error instanceof Error ? error : undefined
          );
        } finally {
          if (!isStale()) {
            this.fetchAvailabilityInFlight = null;
          }
        }
      })();

      this.fetchAvailabilityInFlight = markRaw(fetchRun);
      return fetchRun;
    },

    /**
     * `null` cuando el backend no declaró el campo, la moneda no está en el
     * catálogo o el fetch falló. El consumidor NO debe defaultear a `true`:
     * ver `additionalAccountIntroBullets.util.ts`.
     */
    getAdditionalAccountHasOwnAccountNumber(currency: string): boolean | null {
      return this.availabilityByCurrency[currency?.toUpperCase()]?.hasOwnAccountNumber ?? null;
    },

    /**
     * ML-96 — barrido completo de `GET company/b2b/teams/users` con
     * `accountId`, quedándose con `associatedToAccount === true`.
     *
     * El recorrido es sobre los usuarios de niveles 1-3 (`levels`, ML-168). El
     * backend NO filtra por `accountId`: pagina esos usuarios y recién ahí
     * decora cada fila con `associatedToAccount`. Por eso hay que recorrer
     * las páginas hasta que `last` sea `true` — quedarse con la primera
     * página deja afuera a los asignados que caen más abajo.
     * El barrido es secuencial A PROPÓSITO: no se sabe cuántas páginas hay
     * hasta que la última lo dice, así que esto no es el N+1 de
     * `http-cascade.md` (esa rule exceptúa la serie cuando cada llamada
     * depende del resultado de la anterior).
     *
     * El endpoint EXCLUYE al usuario logueado (`UserRepository.java:117`,
     * `AND u.id <> :b2bUserId`): un admin asignado a la cuenta no se ve a sí
     * mismo ahí, y el conteo de asignados queda corto en uno. Decisión de
     * producto: se acepta.
     *
     * Un fallo a mitad del barrido NO deja una lista parcial en
     * `assignedUsersByAccount`: sin saber si quedaban más páginas por
     * revisar, una lista incompleta afirmaría "esto es todo" sobre datos que
     * no lo son. Se corta ahí, se expone el error de esa cuenta y se
     * descarta el acumulado de esa corrida. Como nunca marca
     * `hasLoadedAssignedUsersByAccount[accountId]` en ese camino, la próxima
     * llamada no lo trata como caché válido y reintenta.
     *
     * Caché por cuenta, no global — la página de gestión puede tener dos
     * cuentas en juego a la vez. Con caché ya poblado (`hasLoadedAssignedUsersByAccount`)
     * una llamada nueva es un no-op y NO vuelve a golpear el endpoint — seis
     * consumidores de UI llaman a esta action al montar, y sin este guard
     * cada uno rebarría el listado completo otra vez
     * (`http-cascade.md`, caso 4). `force: true` lo salta a propósito: es lo
     * que usan los tres refrescos post-escritura (`assignUsersToAccount`,
     * `applyAccountEdit`, `removeUserFromAccount`), donde el caché sigue
     * poblado pero quedó desactualizado por el propio write. `force` también
     * ignora el dedupe de una corrida en vuelo: esa corrida pudo arrancar
     * ANTES del write y traería datos viejos — y ARRANCA UNA CORRIDA PARALELA
     * a propósito.
     *
     * Esa corrida paralela es justo el riesgo: dos barridos para la MISMA
     * cuenta pueden estar en vuelo a la vez (uno normal + uno forzado), y si
     * el viejo resuelve DESPUÉS del forzado, escribiría la lista pre-escritura
     * encima de la fresca. Por eso cada corrida captura un token propio de
     * `fetchAssignedUsersSweepTokenByAccount[accountId]` ANTES de su primer
     * `await` — arrancar ya incrementa el contador e invalida a cualquier
     * corrida anterior para esa cuenta — y lo revalida DESPUÉS de cada
     * `await`. Una corrida cuyo token quedó atrás se descarta en silencio: no
     * escribe lista, no escribe error, no toca `hasLoadedAssignedUsersByAccount`.
     * Es un guard aparte de `isStale()`/`fetchGeneration`, que resuelve el
     * `$reset` del logout — no reemplaza a este, lo complementa.
     *
     * @param options.force - fuerza el barrido aunque el caché ya esté
     * poblado; ver el motivo arriba.
     */
    async fetchAssignedUsers(accountId: number, options: FetchAssignedUsersOptions = {}): Promise<void> {
      const { force = false } = options;

      if (!force && this.hasLoadedAssignedUsersByAccount[accountId]) {
        return;
      }

      const inFlight = this.fetchAssignedUsersInFlightByAccount[accountId];
      if (inFlight && !force) {
        return inFlight;
      }

      const generation = this.fetchGeneration;
      const isStale = (): boolean => generation !== this.fetchGeneration;

      const sweepToken = (this.fetchAssignedUsersSweepTokenByAccount[accountId] ?? 0) + 1;
      this.fetchAssignedUsersSweepTokenByAccount = {
        ...this.fetchAssignedUsersSweepTokenByAccount,
        [accountId]: sweepToken,
      };
      const isSweepSuperseded = (): boolean => this.fetchAssignedUsersSweepTokenByAccount[accountId] !== sweepToken;
      function isDiscarded(): boolean {
        return isStale() || isSweepSuperseded();
      }

      const fetchRun = (async () => {
        const assignedUsersErrorMessage =
          'No pudimos cargar los usuarios asignados a esta cuenta. Vuelve a intentarlo en unos minutos.';

        this.isFetchingAssignedUsersByAccount = { ...this.isFetchingAssignedUsersByAccount, [accountId]: true };
        this.assignedUsersErrorByAccount = { ...this.assignedUsersErrorByAccount, [accountId]: '' };

        try {
          const assignedUsers: AssignedCompanyUserOption[] = [];
          let page = 0;
          let isLastPage = false;
          let didFail = false;

          while (!isLastPage && !didFail) {
            const result = await teamUserService.getTeamUsers({
              page,
              size: ACCOUNT_ASSIGNED_USERS_PAGE_SIZE,
              accountId,
              levels: ACCOUNT_ASSIGNMENT_USER_LEVELS,
            });
            if (isDiscarded()) {
              return;
            }

            result.fold(
              (failure) => {
                didFail = true;
                this.assignedUsersErrorByAccount = {
                  ...this.assignedUsersErrorByAccount,
                  [accountId]: getReasonMessage({ failure, defaultMessage: assignedUsersErrorMessage }),
                };
              },
              (success) => {
                assignedUsers.push(
                  ...success.data.content
                    .filter((user) => user.associatedToAccount === true)
                    .map(getAssignedCompanyUserAdapter)
                );
                // Una página vacía no puede aportar nada más aunque el backend
                // siga diciendo que no es la última: sin este corte, un `last`
                // que nunca llega deja el barrido pidiendo páginas para
                // siempre. Mismo criterio defensivo que el adapter del listado
                // ante un 200 fuera de contrato del proxy.
                isLastPage = success.data.last || success.data.content.length === 0;
              }
            );

            page += 1;
            if (page >= ACCOUNT_ASSIGNED_USERS_MAX_PAGES) {
              isLastPage = true;
            }
          }

          if (didFail) {
            return;
          }
          this.assignedUsersByAccount = { ...this.assignedUsersByAccount, [accountId]: assignedUsers };
          this.hasLoadedAssignedUsersByAccount = { ...this.hasLoadedAssignedUsersByAccount, [accountId]: true };
        } catch (error) {
          // Defensa ante throws inesperados: ver el mismo criterio en
          // `fetchAdditionalAccounts` — la promesa nunca rechaza.
          logger.error(
            'additionalAccount.store: fetchAssignedUsers lanzó fuera del contrato',
            { accountId },
            error instanceof Error ? error : undefined
          );
          if (!isDiscarded()) {
            this.assignedUsersErrorByAccount = {
              ...this.assignedUsersErrorByAccount,
              [accountId]: assignedUsersErrorMessage,
            };
          }
        } finally {
          if (!isDiscarded()) {
            this.isFetchingAssignedUsersByAccount = { ...this.isFetchingAssignedUsersByAccount, [accountId]: false };
            this.fetchAssignedUsersInFlightByAccount = {
              ...this.fetchAssignedUsersInFlightByAccount,
              [accountId]: null,
            };
          }
        }
      })();

      this.fetchAssignedUsersInFlightByAccount = {
        ...this.fetchAssignedUsersInFlightByAccount,
        [accountId]: markRaw(fetchRun),
      };
      return fetchRun;
    },

    /**
     * ML-3 — `POST .../additional-accounts`. Retorna la cuenta creada para que
     * la page caller navegue a su detalle, o `null` si falló (el drawer sigue
     * abierto con el formulario cargado). El toast de ÉXITO no se dispara acá:
     * vive en el aterrizaje del detalle (ML-64). El drawer lo cierra la
     * fachada — es estado por-página.
     *
     * `initialAmount` se omite cuando el usuario eligió "Cargar más tarde":
     * mandar 0 empujaría al backend al camino de fondeo con un monto inválido.
     *
     * Un fondeo fallido NO es un error de creación (ver
     * `CREATE_ADDITIONAL_ACCOUNT_FUNDING_FAILED_TOAST`): la cuenta se agrega
     * igual y se avisa con una advertencia, porque reintentar la creación
     * duplicaría la cuenta.
     *
     * La asignación viaja en el MISMO request, en `userIds`: no hay segunda
     * llamada, así que tampoco hay estado intermedio de "cuenta creada pero sin
     * asignar". Si el create falla, no se asignó nadie.
     *
     * Por eso el `fold` es síncrono: ninguna rama espera nada más
     * (`either-error-handling.md`). Y el `finally` sigue envolviendo todo el
     * bloque para que `isCreatingAccount` cubra la operación completa — si algún
     * día vuelve un `await` acá adentro, el guard de doble submit tiene que
     * seguir cerrado durante ese await.
     */
    async createAccount(payload: CreateAdditionalAccountPayload): Promise<AdditionalAccountWallet | null> {
      if (this.isCreatingAccount) {
        return null;
      }
      this.isCreatingAccount = true;

      try {
        const userIds = toCreatableUserIds(payload.userIds);

        if (userIds.length !== payload.userIds.length) {
          logger.warn('Ids de usuario no numéricos al crear la cuenta adicional', {
            userIds: payload.userIds,
            descartados: payload.userIds.length - userIds.length,
          });
          if (userIds.length === 0) {
            toast.warning(CREATE_ADDITIONAL_ACCOUNT_ASSIGN_FAILED_TOAST);
          }
        }

        const result = await additionalAccountService.createAdditionalAccount({
          currencyCode: payload.currency,
          name: payload.accountName,
          ...(payload.amount > 0 ? { initialAmount: payload.amount } : {}),
          ...(userIds.length > 0 ? { userIds } : {}),
        });

        return result.fold<AdditionalAccountWallet | null>(
          (failure) => {
            toast.error(
              getReasonMessage({
                failure,
                errorMap: ErrorsAdditionalAccount,
                defaultMessage: CREATE_ADDITIONAL_ACCOUNT_ERROR_MESSAGE,
              })
            );
            return null;
          },
          (success) => {
            const newAccount = buildCreatedAdditionalAccountWallet(payload, success.data, this.accounts.length);
            this.accounts = [...this.accounts, newAccount];
            this.locallyCreatedAccountIds = [...this.locallyCreatedAccountIds, newAccount.wallet.walletId];
            this.assignedAccountIds = [...new Set([...this.assignedAccountIds, newAccount.wallet.walletId])];
            if (success.data.initialFunding === 'FAILED') {
              toast.warning(CREATE_ADDITIONAL_ACCOUNT_FUNDING_FAILED_TOAST);
            }
            return newAccount;
          }
        );
      } finally {
        this.isCreatingAccount = false;
      }
    },

    /**
     * `assignments` vacío/ausente = sin reasignar (ML-51 directo). Con
     * asignaciones, `buildAdditionalAccountClosureRequest` decide si el
     * request es `ALL_TO_ONE` (ML-62) o `PER_USER` (ML-63 parcial). Si el
     * builder devuelve `null`, el payload no tiene ids válidos: se aborta
     * ANTES de llamar al endpoint. `cardAssignments` (ML-126) viaja en el body
     * con cualquiera de los tres modos y se omite cuando está vacío.
     */
    async closeAdditionalAccount(
      walletId: number,
      assignments: UserAccountAssignment[] = [],
      cardAssignments: CardAccountReassignment[] = []
    ): Promise<void> {
      // `assignedUsersByAccount[walletId]` es undefined cuando `fetchAssignedUsers`
      // no corrió o falló para esta cuenta — eso NO es "cero usuarios". El -1
      // nunca iguala `assignments.length`, así que fuerza el modo explícito
      // PER_USER en vez de asumir que la reasignación ya cubre a todos (ALL_TO_ONE).
      const assignedUsersCount = this.assignedUsersByAccount[walletId]?.length ?? -1;
      const body = buildAdditionalAccountClosureRequest(assignments, assignedUsersCount, cardAssignments);

      if (!body) {
        toast.error(DELETE_ADDITIONAL_ACCOUNT_ERROR_MESSAGE);
        return;
      }

      const result = await additionalAccountService.closeAdditionalAccount(walletId, body);

      result.fold(
        (failure) => {
          /**
           * WARNING (review, 2026-08-24): ML-51 pide un banner rojo persistente
           * para este error, no un toast — el modal ya se cerró antes de
           * disparar el delete. El copy ya está alineado verbatim con la HU;
           * falta el componente.
           */
          toast.error(
            getReasonMessage({
              failure,
              errorMap: ErrorsAdditionalAccount,
              defaultMessage: DELETE_ADDITIONAL_ACCOUNT_ERROR_MESSAGE,
            })
          );
        },
        () => {
          /**
           * `success.data.reassignedUsers` / `unlinkedUsers` quedan disponibles
           * para un mensaje de éxito más preciso — hoy no hay HU con ese copy.
           */
          this.accounts = this.accounts.filter((account) => account.wallet.walletId !== walletId);
          toast.success(DELETE_ADDITIONAL_ACCOUNT_SUCCESS_TOAST);
        }
      );
    },

    /**
     * ML-96 — asigna N usuarios a la cuenta con el endpoint real
     * (`assignUsersToAdditionalAccount`, una sola llamada con todos los ids).
     * El backend AGREGA afiliaciones, nunca reemplaza: repetir una asignación
     * es idempotente (ver el JSDoc del service). El toast de éxito espera al
     * refresco de `fetchAssignedUsers` — un 200 no garantiza que TODOS
     * quedaron asignados, `filterEligibleUserIds` descarta usuarios no
     * elegibles en silencio del lado del backend — y nunca sale si la
     * llamada fue rechazada.
     * @param userIds - ids del catálogo (`CompanyUserOption.id`, string); los
     * no numéricos se descartan antes de llamar al backend.
     */
    async assignUsersToAccount(walletId: number, userIds: string[]): Promise<void> {
      const numeric = toCreatableUserIds(userIds);
      if (numeric.length === 0) {
        logger.warn('additionalAccount.store: assignUsersToAccount sin ids numéricos válidos', {
          walletId,
          userIds,
        });
        return;
      }

      const result = await additionalAccountService.assignUsersToAdditionalAccount(walletId, numeric);

      await result.foldAsync(
        async (failure) => {
          toast.error(getReasonMessage({ failure, errorMap: ErrorsAdditionalAccount }));
        },
        async () => {
          await this.fetchAssignedUsers(walletId, { force: true });
          toast.success(ASSIGN_ADDITIONAL_ACCOUNT_USERS_SUCCESS_TOAST);
        }
      );
    },

    /**
     * Edición batch (ML-50): reemplaza el nombre local en UNA sola reasignación
     * — el rename real (ML-40) ya ocurrió antes de llegar acá — y sincroniza la
     * selección de usuarios contra el backend real (ML-96): el diff sale de
     * comparar `edit.userIds` contra el caché de `fetchAssignedUsers`
     * (`assignedUsersByAccount[walletId]`), y viaja como un POST de altas
     * (`assignUsersToAdditionalAccount`) más un `unassignUserFromAccount` por
     * cada baja, todos en paralelo — son operaciones independientes entre sí
     * (`http-cascade.md`), nunca en serie. Si la cuenta nunca se leyó en esta
     * sesión el diff parte de una lista vacía: puede reasignar de más (inocuo,
     * es idempotente) pero no detecta bajas hasta que exista caché. SIN toast
     * propio: el toast único lo dispara el drawer de edición.
     *
     * El `Promise.all` no es atómico: devuelve `{ didApplyAllChanges }` para
     * que el LLAMADOR decida qué hacer con un fallo parcial (alguna alta o
     * baja rechazada por el backend) — antes ese caso solo quedaba en el
     * logger, y el drawer tosteaba éxito y cerraba igual sobre un estado que
     * no reflejaba lo que el usuario pidió.
     */
    async applyAccountEdit(
      walletId: number,
      edit: AdditionalAccountEditPayload
    ): Promise<AdditionalAccountEditResult> {
      this.accounts = this.accounts.map((account) => {
        if (account.wallet.walletId !== walletId) {
          return account;
        }
        return { ...account, accountName: edit.accountName };
      });

      const currentUserIds = (this.assignedUsersByAccount[walletId] ?? []).map((user) => user.id);
      const addedUserIds = toCreatableUserIds(edit.userIds.filter((userId) => !currentUserIds.includes(userId)));
      const removedUserIds = toCreatableUserIds(
        currentUserIds.filter((userId) => !edit.userIds.includes(userId))
      );

      if (addedUserIds.length === 0 && removedUserIds.length === 0) {
        return { didApplyAllChanges: true };
      }

      const results = await Promise.all([
        ...(addedUserIds.length > 0
          ? [additionalAccountService.assignUsersToAdditionalAccount(walletId, addedUserIds)]
          : []),
        ...removedUserIds.map((userId) => additionalAccountService.unassignUserFromAccount(walletId, userId)),
      ]);

      const didApplyAllChanges = !results.some((result) => result.isLeft());

      if (!didApplyAllChanges) {
        logger.error('additionalAccount.store: applyAccountEdit no pudo sincronizar el diff completo con el backend', {
          walletId,
          addedUserIds,
          removedUserIds,
        });
      }

      await this.fetchAssignedUsers(walletId, { force: true });

      return { didApplyAllChanges };
    },

    /**
     * ML-96 — revoca el acceso de un usuario con el endpoint real
     * (`unassignUserFromAccount`). Revocar algo ya inactivo es un no-op para
     * el backend (200 igual, ver el JSDoc del service), así que reintentar no
     * es un error. Mismo criterio que `assignUsersToAccount`: refresca
     * `fetchAssignedUsers` y solo tostea éxito si el backend confirmó.
     */
    async removeUserFromAccount(walletId: number, userId: string): Promise<void> {
      const numeric = toCreatableUserIds([userId]);
      if (numeric.length === 0) {
        logger.warn('additionalAccount.store: removeUserFromAccount con un id no numérico', { walletId, userId });
        return;
      }

      const result = await additionalAccountService.unassignUserFromAccount(walletId, numeric[0]);

      await result.foldAsync(
        async (failure) => {
          toast.error(
            getReasonMessage({
              failure,
              errorMap: ErrorsAdditionalAccount,
              defaultMessage: UNASSIGN_USER_ACCOUNT_ERROR_MESSAGE,
            })
          );
        },
        async () => {
          await this.fetchAssignedUsers(walletId, { force: true });
          toast.success(REMOVE_ADDITIONAL_ACCOUNT_USER_SUCCESS_TOAST);
        }
      );
    },
  },
});
